import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { BrandMark } from "@/components/BrandLogo";

// Confirmation page. Access is NEVER granted from reaching this page — we poll
// the backend, which only reports "activated" once Stripe confirms the payment
// (via webhook or a verified session lookup).
export default function BillingSuccess() {
  const nav = useNavigate();
  const org = useOrg();
  const [params] = useSearchParams();
  const [state, setState] = useState("checking"); // checking | activated | timeout | error
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/billing/session/${sessionId}`);
        if (data.activated) {
          setState("activated");
          org?.refresh?.();
          return;
        }
      } catch (e) {
        if (tries.current >= 3) { setState("error"); return; }
      }
      if (tries.current >= 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-black bg-noise px-5 text-white">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl border border-gold/20 bg-charcoal p-10 text-center" data-testid="billing-success">
        <div className="flex justify-center"><BrandMark size={64} glow /></div>

        {state === "checking" && (
          <>
            <Loader2 className="mx-auto mt-8 h-7 w-7 animate-spin text-gold" />
            <h1 className="mt-5 font-heading text-2xl font-bold">Payment received</h1>
            <p className="mt-2 text-white/55" data-testid="billing-success-status">Activating BDVG AI Pro…</p>
          </>
        )}

        {state === "activated" && (
          <>
            <CheckCircle2 className="mx-auto mt-8 h-9 w-9 text-gold" />
            <h1 className="mt-5 font-heading text-2xl font-bold text-gradient-gold">BDVG AI Pro activated</h1>
            <p className="mt-2 text-white/55">Your workspace is now on BDVG AI Pro. Enjoy the full power of BDVG AI.</p>
            <button onClick={() => nav("/app/dashboard")} className="mt-7 w-full rounded-xl bg-gradient-to-r from-gold-light to-gold py-3 font-semibold text-black" data-testid="billing-success-continue">Go to Dashboard</button>
          </>
        )}

        {state === "timeout" && (
          <>
            <Loader2 className="mx-auto mt-8 h-7 w-7 animate-spin text-gold/60" />
            <h1 className="mt-5 font-heading text-2xl font-bold">Almost there</h1>
            <p className="mt-2 text-white/55">Your payment is confirmed and we're finalising your subscription. This can take a moment — you can head to your dashboard and it will activate automatically.</p>
            <button onClick={() => nav("/app/billing")} className="mt-7 w-full rounded-xl border border-gold/30 py-3 font-semibold text-gold" data-testid="billing-success-manage">View Billing</button>
          </>
        )}

        {state === "error" && (
          <>
            <AlertTriangle className="mx-auto mt-8 h-8 w-8 text-white/50" />
            <h1 className="mt-5 font-heading text-2xl font-bold">We couldn't confirm this checkout</h1>
            <p className="mt-2 text-white/55">If you completed payment, your plan will activate shortly. Check your billing page or contact support if anything looks wrong.</p>
            <button onClick={() => nav("/app/billing")} className="mt-7 w-full rounded-xl border border-white/15 py-3 text-white/80" data-testid="billing-success-billing">Go to Billing</button>
          </>
        )}
      </motion.div>
    </div>
  );
}
